/**
 * A hardcoded demo song to verify the scheduler + engine.
 * Once the pattern editor is wired up, this becomes a preset rather than
 * required code. For now it's our smoke test for "music plays".
 *
 * Style: A minor, 150 BPM, 4 rows/beat → 10 rows/sec. 16-row pattern × 2
 * sections gives ~3.2 seconds of music. The melody walks i-VI-VII-i over
 * one bar each, with bass on triangle and a simple drum pattern.
 *
 * NB: this uses our channel APIs directly, not the Project file format.
 * The engine will load Project data the same way once we have the
 * pattern editor.
 */

export interface DemoCell {
  /** Note name (e.g. "C-5") or 'OFF' or null. */
  note?: string | null;
  /** Pulse: duty 0..3. */
  duty?: 0 | 1 | 2 | 3;
  /** Volume 0..15 (where applicable). */
  volume?: number;
}

export interface DemoNoiseCell {
  note?: string | null; // present = noteOn, 'OFF' = noteOff
  periodIndex?: number;
  mode?: 'long' | 'short';
  volume?: number;
  /** Optional per-trigger volume decay in ms (0/undefined = sustained). */
  decayMs?: number;
}

export interface DemoDPCMCell {
  /** Sample name to trigger ('kick', 'snare'), or null for nothing. */
  trigger?: 'kick' | 'snare' | null;
  rateIndex?: number;
  volume?: number;
}

export interface DemoRow {
  pulse1?: DemoCell;
  pulse2?: DemoCell;
  triangle?: DemoCell;
  noise?: DemoNoiseCell;
  dpcm?: DemoDPCMCell;
}

export interface DemoSong {
  title: string;
  rowsPerPattern: number;
  patterns: DemoRow[][];
  orderList: number[];
  loopStartOrderIndex: number | null;
}

/** Helpers to keep the song table readable. */
const ON = (note: string, extra: Partial<DemoCell> = {}): DemoCell => ({ note, volume: 12, ...extra });
const OFF = (): DemoCell => ({ note: 'OFF' });
const KICK: DemoDPCMCell = { trigger: 'kick', rateIndex: 13, volume: 15 };
const SNARE: DemoDPCMCell = { trigger: 'snare', rateIndex: 13, volume: 14 };
const HAT_CLOSED: DemoNoiseCell = { note: 'on', periodIndex: 2, mode: 'long', volume: 4 };
const HAT_OFF: DemoNoiseCell = { note: 'OFF' };

/**
 * Pattern A: i (Am) - VI (F) - VII (G) - i (Am), 16 rows = 4 beats.
 * Beat 1 starts at row 0; rows 0,4,8,12 are beats.
 */
const PATTERN_A: DemoRow[] = [
  // beat 1 — A minor
  { pulse1: ON('A-5', { duty: 1 }), triangle: ON('A-2'), dpcm: KICK, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('C-6'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 2 — A minor cont.
  { pulse1: ON('E-6'), triangle: ON('E-3'), dpcm: SNARE, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('D-6'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 3 — F major
  { pulse1: ON('C-6'), triangle: ON('F-2'), dpcm: KICK, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('A-5'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 4 — G major
  { pulse1: ON('B-5'), triangle: ON('G-2'), dpcm: SNARE, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('D-6'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED, pulse1: OFF() },
];

/**
 * Pattern B: descending lead variation, same chord shape.
 */
const PATTERN_B: DemoRow[] = [
  // beat 1
  { pulse1: ON('E-6'), triangle: ON('A-2'), dpcm: KICK, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('D-6'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 2
  { pulse1: ON('C-6'), triangle: ON('E-3'), dpcm: SNARE, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('B-5'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 3
  { pulse1: ON('A-5'), triangle: ON('F-2'), dpcm: KICK, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('C-6'), noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  // beat 4 — V (E maj with G# leading tone) → resolves to A minor in pattern A
  { pulse1: ON('B-5'), triangle: ON('E-2'), dpcm: SNARE, noise: HAT_CLOSED },
  { noise: HAT_CLOSED },
  { pulse1: ON('G#5'), noise: HAT_CLOSED },
  { noise: HAT_OFF, pulse1: OFF() },
];

/** Repeat a 16-row "bar" four times to make a 64-row pattern. */
function tile(rows: DemoRow[], times: number): DemoRow[] {
  const out: DemoRow[] = [];
  for (let i = 0; i < times; i++) out.push(...rows.map((r) => ({ ...r })));
  return out;
}

export const DEMO_SONG: DemoSong = {
  title: 'Steel Gauntlet (demo)',
  rowsPerPattern: 64,
  patterns: [tile(PATTERN_A, 4), tile(PATTERN_B, 4)],
  orderList: [0, 1, 0, 1],
  loopStartOrderIndex: 0,
};
